<script setup lang="ts">
import { onMounted, ref } from 'vue'

import UiButton from '@/components/UiButton.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { RebindRequest } from '@/api/ls'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const dialogs = useDialogsStore()

onMounted(async () => {
  await Promise.all([store.ensureCustomers(), store.loadRebinds()])
})

async function approve(r: RebindRequest) {
  if (!(await dialogs.confirm({ message: '核准換機申請？將簽發綁定新硬體的新授權、舊授權標 superseded。' }))) return
  try {
    await ls.approveRebind(r.id)
    await Promise.all([store.loadRebinds(), store.loadLicenses()])
  } catch (e) {
    const msg = e instanceof ApiError && e.payload ? JSON.stringify((e.payload as { detail?: unknown }).detail ?? e.payload) : '核准失敗'
    await dialogs.notify({ title: '操作失敗', message: msg })
  }
}
/**
 * keyset 分頁（批93-D LS-4）：後端一頁 50 筆，`store.rebindsNextCursor` 非 null＝還有。
 * 換機申請是只增不刪的紀錄表，所以清單頁一定要有續頁的出口。
 */
const loadingMore = ref(false)
async function loadMore() {
  if (!store.rebindsNextCursor || loadingMore.value) return
  loadingMore.value = true
  try {
    await store.loadMoreRebinds()
  } catch {
    await dialogs.notify({ title: '操作失敗', message: '讀取下一頁失敗' })
  } finally {
    loadingMore.value = false
  }
}
function statusClass(s: string): string {
  return { approved: 'active', rejected: 'revoked', pending: 'pending' }[s] ?? 'pending'
}
function statusLabel(s: string): string {
  return { pending: '審核中', approved: '已核准', rejected: '已婉拒' }[s] ?? s
}
</script>

<template>
  <div class="page">
    <div class="page-head"><h1>換機申請</h1><span class="count">{{ store.rebinds.length }}</span></div>
    <div class="card">
      <div v-if="!store.rebinds.length" class="empty"><div class="empty-sub">目前沒有換機申請。</div></div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr><th>客戶</th><th>舊授權</th><th>新指紋</th><th>狀態</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="r in store.rebinds" :key="r.id">
              <td>{{ store.customerName(r.customer_id) }}</td>
              <td><code class="mono">{{ r.old_license_id?.slice(0, 8) ?? '—' }}</code></td>
              <td><code class="mono">{{ r.new_fingerprint ?? '—' }}</code></td>
              <td><span class="status-pill" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span></td>
              <td>
                <UiButton v-if="r.status === 'pending'" variant="primary" size="sm" @click="approve(r)">核准</UiButton>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- keyset 分頁：後端一頁 50 筆。`next_cursor` 非 null 才畫這一列。 -->
      <div v-if="store.rebindsNextCursor" class="more">
        <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
        <span class="subtle">已載入 {{ store.rebinds.length }} 筆</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
/* keyset 分頁的「載入更多」列（形狀與 LicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
</style>
